import React, { useEffect, useState } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { DropDownPickerProps, ItemType } from 'react-native-dropdown-picker';
import MyDropDownPicker, { ValueType } from './MyDropDownPicker';
import MyAppText from './MyAppText';
import Badge from './Badge';
import { ScrollView } from 'react-native-web-hover';
import getBestContrastColor from '../services/best-contrast-color';
import Tooltip from './Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import inputStylesheet from '../styles/inputStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DistributiveOmit<T, K extends keyof any> = T extends any
    ? Omit<T, K>
    : never;


type Props = DistributiveOmit<DropDownPickerProps<ValueType>, 'open' | 'setOpen'>
    & {
        label?: string;
        labelFontSize?: number;
        labelStyle?: StyleProp<TextStyle>;
        viewStyle?: StyleProp<TextStyle>;
        selectedLabel?: string;
        selectedLabelFontSize?: number;
        selectedLabelStyle?: StyleProp<TextStyle>;
        onRemove?: (items: ValueType[]) => void;
        horizontalScroll?: boolean;
        scrollMaxHeight?: number;
        initialSelectedItems?: CustomType[];
        badgeTextColor?: string;
        hintText?: string;
        readOnly?: boolean;
        required?: boolean;
    };

type CustomType = ItemType<ValueType> & {
    color?: string;
    textColor?: string;
};

export default function ExternalSelect({
    label,
    selectedLabel,
    viewStyle,
    selectedLabelFontSize,
    selectedLabelStyle,
    onRemove,
    horizontalScroll,
    scrollMaxHeight = 80,
    initialSelectedItems,
    badgeTextColor,
    hintText,
    readOnly,
    required,
    ...props
}: Props) {
    const { styles, theme } = useStyles(stylesheet);
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    const [isOpen, setIsOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<CustomType[]>([]);

    useEffect(() => {
        if (initialSelectedItems) {
            setSelectedItems(initialSelectedItems);
        }
    }, [initialSelectedItems]);

    function onSelectItem(item: CustomType | CustomType[]) {
        if (!Array.isArray(item)) {
            const index = selectedItems.findIndex(i => i.value === item.value);

            if (index !== -1) {
                const newItems = [...selectedItems];
                newItems.splice(index, 1);
                setSelectedItems(newItems);
            } else {
                setSelectedItems([{ value: item.value as number ?? 0, label: item.label ?? '', color: item.color ?? '', textColor: item.textColor ?? undefined }]);
            }
        } else {
            setSelectedItems([...item.map(i => {
                return { value: i.value as number ?? 0, label: i.label ?? '', color: i.color ?? '', textColor: i.textColor ?? undefined };
            })]);
        }
    }

    function removeItem(index: number) {
        const newItems = [...selectedItems];
        newItems.splice(index, 1);
        setSelectedItems(newItems);
        if (onRemove) {
            onRemove(newItems.map(i => i.value ?? 0));
        }
    }

    return (
        <View style={[styles.inputContainer(props.zIndex), viewStyle]}>
            {!readOnly && <View style={[defaultInputStyle.container, hintText ? { zIndex: 9999 } : { zIndex: props.zIndex }]}>
                <View style={[defaultInputStyle.labelContainer, hintText ? { zIndex: 9999 } : {}]}>
                    {label && <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>}
                    {required ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                    {hintText
                        ? <Tooltip
                            useSimpleBox
                            style={{
                                color: theme.colors.white['500'],
                                padding: 16,
                                borderRadius: 12,
                                justifyContent: 'center',
                                zIndex: 9999,
                                background: theme.colors.neutral['500']
                            }}
                            tooltipContent={hintText}
                            tooltipDirection='right'
                        >
                            <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['500']} />
                        </Tooltip>
                        : null
                    }
                </View>
                <MyDropDownPicker
                    {...props}
                    searchable={true}
                    open={isOpen}
                    setOpen={setIsOpen}
                    dropDownDirection={props.dropDownDirection ?? 'BOTTOM'}
                    mode='SIMPLE'
                    labelStyle={{ color: 'transparent' }}
                    onSelectItem={onSelectItem}
                />
            </View>}
            <View style={{ marginTop: 8, gap: 8 }}>
                {selectedLabel ? <MyAppText style={selectedLabelStyle ? selectedLabelStyle : styles.selectedLabel(selectedLabelFontSize ?? 14)}>{selectedLabel}</MyAppText> : null}
                <ScrollView horizontal={horizontalScroll} style={{ maxHeight: scrollMaxHeight }} contentContainerStyle={{ flexDirection: 'row', columnGap: 8, rowGap: 4, flexWrap: 'wrap' }}>
                    {selectedItems.map((item, index) => (
                        <Badge
                            key={index}
                            mode='ROUNDED'
                            backgroundColor={item.color}
                            textColor={
                                badgeTextColor
                                    ? badgeTextColor
                                    : item.textColor
                                        ? item.textColor
                                        : getBestContrastColor(item.color ?? '')
                            }
                            removable={!readOnly}
                            onRemove={() => removeItem(index)}
                        >
                            {item.label}
                        </Badge>
                    ))}
                </ScrollView>
            </View>
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: (zIndex: number | undefined) => ({
        zIndex,
        minWidth: 200
    }),
    filterText: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
    selectedLabel: (fontSize: number) => ({
        fontSize,
        color: theme.colors.primary['500'],
        fontWeight: '600',
        textTransform: 'uppercase',
    })
}));
